import { Sound, SoundManager } from '@1x2network/howler_sound'
import { CreateSoundOptions } from '@1x2network/howler_sound/lib/sound/createSound'
import { Specification } from '@1x2network/pixi_assets'
import gsap from 'gsap'
import { BackgroundMusic, BackgroundMusicSpecification } from '../utils/BackgroundMusic'

/**
 * Class for playing sounds
 * @author Adam Drake - <adam.drake@1x2network.com>
 * @version 1.0.0
 */
export class SoundPlayer extends SoundManager {
    private specification: SoundSpecification
    private background: BackgroundMusic
    private currentTier = -1
    private transition: Sound
    private hawStart: Sound
    private hawEnd: Sound
    private winTiers: Sound[]
    private symbolWin: Sound[]
    private reelLand: Sound
    private spinStart: Sound
    private logoUpgrade: Sound
    private logoAnimate: Sound
    private scatterLand: Sound
    private collectorLand: Sound
    private jackpotLand: Sound
    private scatterTrigger: Sound
    private collectorTrigger: Sound
    private coinShowerAnticipate: Sound
    private coinShowerEmit: Sound
    private coinShowerLand: Sound
    private hawSummaryIncrease: Sound
    private collectorIncrease: Sound
    private countUpLoop: Sound
    private countUpEnd: Sound
    private anticipation: Sound
    private anticipationMiss: Sound
    private introAnimationWidgetLand: Sound
    private introAnimation: Sound

    private _debounce = new Map<string, number>()

    constructor() {
        super()
        this.specification = Specification.get('sound') as SoundSpecification
        this.background = new BackgroundMusic(this.specification.background)
        this.transition = new Sound(this.specification.transition)
        this.hawStart = new Sound(this.specification.hawStart)
        this.hawEnd = new Sound(this.specification.hawEnd)
        this.winTiers = this.specification.winTiers.map((spec) => new Sound(spec))
        this.symbolWin = this.specification.symbolWin.map((spec) => new Sound(spec))
        this.reelLand = new Sound(this.specification.reelLand)
        this.spinStart = new Sound(this.specification.spinStart)
        this.logoUpgrade = new Sound(this.specification.logoUpgrade)
        this.logoAnimate = new Sound(this.specification.logoAnimate)
        this.scatterLand = new Sound(this.specification.scatterLand)
        this.collectorLand = new Sound(this.specification.collectorLand)
        this.jackpotLand = new Sound(this.specification.jackpotLand)
        this.scatterTrigger = new Sound(this.specification.scatterTrigger)
        this.collectorTrigger = new Sound(this.specification.collectorTrigger)
        this.coinShowerAnticipate = new Sound(this.specification.coinShowerAnticipate)
        this.coinShowerEmit = new Sound(this.specification.coinShowerEmit)
        this.coinShowerLand = new Sound(this.specification.coinShowerLand)
        this.hawSummaryIncrease = new Sound(this.specification.hawSummaryIncrease)
        this.collectorIncrease = new Sound(this.specification.collectorIncrease)
        this.countUpLoop = new Sound(this.specification.countUpLoop)
        this.countUpEnd = new Sound(this.specification.countUpEnd)
        this.anticipation = new Sound(this.specification.anticipation)
        this.anticipationMiss = new Sound(this.specification.anticipationMiss)
        this.introAnimationWidgetLand = new Sound(this.specification.introAnimationWidgetLand)
        this.introAnimation = new Sound(this.specification.introAnimation)
    }

    private debounce(sound: Sound): boolean {
        const { key, debounce = 0 } = (sound as unknown as { _options: CreateSoundOptions })._options
        const lastPlayTime = this._debounce.get(key) ?? -Infinity

        const now = performance.now()
        const timeSinceLastPlay = now - lastPlayTime
        const hasEnoughTimePassed = timeSinceLastPlay >= debounce

        if (hasEnoughTimePassed) {
            this._debounce.set(key, now)
        }

        return !hasEnoughTimePassed
    }

    public toggleSounds(enabled: boolean): void {
        this.enabled = enabled
    }

    public setTier(tier: number): void {
        if (this.currentTier === tier) return
        this.background.setTier(tier)
    }

    public playWinTier(tier: number) {
        const sound = this.winTiers[tier]
        sound.play()
    }

    public playWin(ids: number[]): void {
        const orderedIds = ids.sort().reverse()
        const soundId = orderedIds[0]
        const symbolWin = this.symbolWin[soundId]
        symbolWin.play()
    }

    public playReelLand(): void {
        this.reelLand.play()
    }

    public playSpinStart(): void {
        this.spinStart.play()
    }

    public fadeBackground(volume: number): void {
        this.background.fade(volume, 0.3)
    }

    public stopWinTier(tier: number) {
        const sound = this.winTiers[tier]
        if (!sound) return

        gsap.to(sound, {
            volume: 0,
            duration: 0.3,
            onComplete: () => {
                sound.stop()
                sound.volume = 1
            },
        })
    }

    public playLogoUpgrade(): void {
        this.logoUpgrade.play()
    }

    public playTransitionIn(): void {
        this.transition.play()
        this.transition.seek(0.35)
    }

    public playTransitionOut(): void {
        this.transition.play()
        this.transition.seek(0.3)
    }

    public playLogoAnimate(): void {
        this.logoAnimate.play()
    }

    public playScatterLand(i: number): void {
        if (this.debounce(this.scatterLand)) {
            return
        }
        this.scatterLand = new Sound(this.specification.scatterLand)
        this.scatterLand.play()
        this.scatterLand.rate = 1 + i * 0.04
    }

    public playCollectorLand(i: number): void {
        if (this.debounce(this.collectorLand)) {
            return
        }
        this.collectorLand = new Sound(this.specification.collectorLand)
        this.collectorLand.play()
        this.collectorLand.rate = 1 + i * 0.04
    }

    public playJackpotLand(i: number): void {
        if (this.debounce(this.jackpotLand)) {
            return
        }
        this.jackpotLand = new Sound(this.specification.jackpotLand)
        this.jackpotLand.play()
        this.jackpotLand.rate = 1 + i * 0.04
    }

    public playScatterTrigger(): void {
        this.scatterTrigger.play()
    }

    public playCollectorTrigger(): void {
        this.collectorTrigger.play()
    }

    public playCoinShowerAnticipate(): void {
        this.coinShowerAnticipate.play()
    }

    public playCoinShowerEmit(): void {
        this.coinShowerEmit.play()
    }

    public playCoinShowerLand(): void {
        this.coinShowerLand.play()
    }

    public playHawStart(): void {
        this.hawStart.play()
    }

    public playHawEnd(): void {
        this.hawEnd.play()
    }

    public playHawSummaryIncrease(): void {
        this.hawSummaryIncrease.play()
    }

    public playCollectorIncrease(i: number): void {
        if (this.debounce(this.collectorIncrease)) {
            return
        }
        this.collectorIncrease = new Sound(this.specification.collectorIncrease)
        this.collectorIncrease.play()
        this.collectorIncrease.rate = 1 + i * 0.02
    }

    public playCountUpLoop(): void {
        this.countUpLoop.play()
    }

    public stopCountUpLoop(): void {
        this.countUpLoop.stop()
    }

    public playCountUpEnd(): void {
        this.countUpEnd.play()
    }

    public playAnticipation(): void {
        if (!this.anticipation.playing) {
            this.anticipation.play()
        }
    }

    public stopAnticipation(): void {
        this.anticipation.stop()
    }

    public playAnticipationMiss(): void {
        this.anticipationMiss.play()
    }

    public playIntroAnimationWidgetLand(): void {
        this.introAnimationWidgetLand.play()
    }

    public playIntroAnimation(): void {
        this.introAnimation.play()
    }
}

export interface SoundSpecification {
    background: BackgroundMusicSpecification
    transition: CreateSoundOptions
    hawStart: CreateSoundOptions
    hawEnd: CreateSoundOptions
    winTiers: CreateSoundOptions[]
    reelLand: CreateSoundOptions
    spinStart: CreateSoundOptions
    symbolWin: CreateSoundOptions[]
    logoUpgrade: CreateSoundOptions
    logoAnimate: CreateSoundOptions
    scatterLand: CreateSoundOptions
    collectorLand: CreateSoundOptions
    jackpotLand: CreateSoundOptions
    scatterTrigger: CreateSoundOptions
    collectorTrigger: CreateSoundOptions
    coinShowerAnticipate: CreateSoundOptions
    coinShowerEmit: CreateSoundOptions
    coinShowerLand: CreateSoundOptions
    hawSummaryIncrease: CreateSoundOptions
    collectorIncrease: CreateSoundOptions
    countUpLoop: CreateSoundOptions
    countUpEnd: CreateSoundOptions
    anticipation: CreateSoundOptions
    anticipationMiss: CreateSoundOptions
    introAnimationWidgetLand: CreateSoundOptions
    introAnimation: CreateSoundOptions
}
